import type { TicketUpdate } from '@/types/parentTicket'

const MAX_THREAD_DEPTH = 12

/** Normalize nested replies from API (array or Laravel wrapper). */
export function normalizeUpdate(raw: TicketUpdate): TicketUpdate {
  let replies: TicketUpdate[] | undefined
  const r = raw.replies as TicketUpdate[] | { data?: TicketUpdate[] } | undefined
  if (Array.isArray(r)) {
    replies = r.map(normalizeUpdate)
  } else if (r && typeof r === 'object' && Array.isArray(r.data)) {
    replies = r.data.map(normalizeUpdate)
  }
  return {
    ...raw,
    parent_id: raw.parent_id ?? raw.parentId ?? null,
    parentId: raw.parent_id ?? raw.parentId ?? null,
    replies,
  }
}

/** Flatten API payload (nested or flat) into a single list. */
export function flattenUpdates(updates: TicketUpdate[]): TicketUpdate[] {
  const out: TicketUpdate[] = []
  const seen = new Set<number>()

  const walk = (u: TicketUpdate) => {
    const n = normalizeUpdate(u)
    if (seen.has(n.id)) return
    seen.add(n.id)
    const { replies, ...rest } = n
    out.push({ ...rest, replies: undefined } as TicketUpdate)
    replies?.forEach(walk)
  }

  updates.forEach(walk)
  return out
}

export function isCommentType(u: TicketUpdate) {
  return u.type === 'comment' || u.type === 'note'
}

export type CommentTreeResult = {
  /** Root comments (newest first), each with recursively nested replies */
  commentThreads: TicketUpdate[]
  /** Root activity items (newest first), each with nested replies */
  activityThreads: TicketUpdate[]
}

function buildSubtree(
  all: TicketUpdate[],
  parentId: number | null,
  rootFilter: (u: TicketUpdate) => boolean,
  depth = 0
): TicketUpdate[] {
  if (depth >= MAX_THREAD_DEPTH) return []

  return all
    .filter((u) => {
      const pid = u.parent_id ?? u.parentId ?? null
      if (pid !== parentId) return false
      if (parentId === null) return rootFilter(u)
      return true
    })
    .sort((a, b) => {
      const order = parentId === null ? -1 : 1
      const da = new Date(a.createdAt ?? a.created_at).getTime()
      const db = new Date(b.createdAt ?? b.created_at).getTime()
      return parentId === null ? db - da : da - db
    })
    .map((u) => ({
      ...u,
      replies: buildSubtree(all, u.id, rootFilter, depth + 1),
    }))
}

/**
 * Build unlimited-depth nested trees from a flat list of updates.
 */
export function buildCommentTree(updates: TicketUpdate[]): CommentTreeResult {
  const all = flattenUpdates(updates)

  return {
    commentThreads: buildSubtree(all, null, isCommentType),
    activityThreads: buildSubtree(all, null, (u) => !isCommentType(u)),
  }
}

export function countThreadNodes(threads: TicketUpdate[]): number {
  let n = 0
  const walk = (list: TicketUpdate[]) => {
    for (const u of list) {
      n += 1
      if (u.replies?.length) walk(u.replies)
    }
  }
  walk(threads)
  return n
}

export { MAX_THREAD_DEPTH }
